
import { Purchase } from "@/models/base";
import AppPurchaseEditLedger from "./PurchaseEditLedger.vue";
import AppPurchaseEditLedgers from "./PurchaseEditLedgers.vue";
import AppPurchaseEditLedgersMobile from "./PurchaseEditLedgersMobile.vue";
import AppPurchaseDocuments from "./PurchaseDocuments.vue";
import { PurchaseLedger } from "@/models/base/purchase-ledger";
import { FormLike } from "winfakt-vue-components/src/mixins/form-like";
import { printHumanFloat } from "@/utils/pretty-print";
import { SupplierService } from "@/services/supplier-service";
import { LedgerAccountService } from "@/services/ledger-account-service";
import { DossierConfigService } from "@/services";
import { IPurchaseLedgerFavorite } from "@/models/dossier-configs/bookkeeping";
import { ISelectItem } from "winfakt-vue-components";

export default FormLike.extend({
	data(){
		return {
			toEditLedger: new PurchaseLedger(),
			p: new Purchase(),
			bookkeepingConfig: DossierConfigService.getBookkeepingConfig(),
			selectedFavoriteId: 0,
			showFavoriteNameModal: false,
			favoriteName: "",
			savingFavorite: false
		};
	},
	props: {
		purchase: {
			type: Object as ()=>Purchase,
			required: true
		}
	},
	watch: {
		purchase: {
			immediate: true,
			deep: true,
			handler(){
				this.p = new Purchase(this.purchase.getJSON());
			}
		}
	},
	computed: {
		hasDocuments():boolean {
			return (this.purchase.Documents?.length > 0) || (this.purchase.UnprocessedFiles?.length > 0);
		},
		totalBooked():string {
			return printHumanFloat(this.p.getLedgersTotal()/this.purchase.CurrencyRate);
		},
		favorites():IPurchaseLedgerFavorite[]{
			return this.bookkeepingConfig.PurchaseLedgerFavorites;
		},
		favoriteItems():ISelectItem<number>[]{
			return this.favorites.map(f=>{
				return {text: f.Name, value: f.ID} as ISelectItem<number>;
			});
		}
	},
	methods: {
		validate(){
			this.isValid = true;
			if (this.p.Ledgers.length == 0){
				this.isValid = false;
			}
			if (!this.p.doLedgersMatchTotal()) {
				this.isValid = false;
			}
		},
		cancel(){
			this.$emit("close");
		},
		async confirm(){
			this.purchase.Ledgers = this.p.Ledgers.map(p=>new PurchaseLedger(p.getJSON()));
			await this.askToSetDefaultLedgerInSupplier();
			this.$emit("close");
		},
		async askToSetDefaultLedgerInSupplier(){
			if (!this.purchase.Contact) return;
			if (this.purchase.Contact.DefaultLedgerAccountCode) return;
			if (this.purchase.Ledgers.length != 1) return;
			let supplier = this.purchase.Contact.getDisplayName();
			let code =  this.purchase.Ledgers[0].LedgerCode;
			let msg = this.$ct("do-you-want-to-set-the-default-ledger-account-of-supplier-to-code", {supplier, code});
			let confirm = await this.$confirmBuilder().setMessage(msg).setTitle(this.$ct("common.info")).setClass("info").show();
			if (!confirm) return;
			let contact = await SupplierService.getContact(this.purchase.ContactID);
			contact.DefaultLedgerAccountCode = code;
			await SupplierService.putContact(contact);
		},
		onInput(value:PurchaseLedger){
			let i = this.p.Ledgers.indexOf(this.toEditLedger);
			if (i == -1) {
				this.p.Ledgers.push(value);
			}else{
				this.p.Ledgers[i] = value;
			}
			this.p.Ledgers = [...this.p.Ledgers];
			this.setNewEmptyEditLedgerValue();
			this.validate();
		},
		setNewEmptyEditLedgerValue(){
			let toEditLedger = new PurchaseLedger();
			toEditLedger.Amount = this.getRemainingAmount();
			this.toEditLedger = toEditLedger;
		},
		getRemainingAmount():number {
			console.log(this.p.getSupposedLedgersTotal(),this.p.getLedgersTotal());
			return this.p.getSupposedLedgersTotal() - this.p.getLedgersTotal();
		},
		onItemSelected(value:PurchaseLedger | null) {
			if (!value) {
				this.setNewEmptyEditLedgerValue();
				return;
			}
			this.toEditLedger = value;
		},
		removeLedger(){
			let i = this.p.Ledgers.indexOf(this.toEditLedger);
			if (i == -1) return;
			this.p.Ledgers.splice(i, 1);
			this.setNewEmptyEditLedgerValue();
			this.validate();
		},
		async applyFavorite(id:number){
			this.selectedFavoriteId = id;
			let favorite = this.favorites.find(f=>f.ID == id);
			if (!favorite) return;
			if (this.p.Ledgers.length) {
				let confirm = await this.$confirmBuilder(this.$ct("favorites.replace-rows-confirm")).setWarningTitle().show();
				if (!confirm) return;
			}
			let accounts = (await LedgerAccountService.fetchLedgerAccounts()).filter(a=>!a.isGroup());
			let remaining = this.p.getSupposedLedgersTotal();
			this.p.Ledgers = favorite.Rows.map(r=>{
				let ledger = new PurchaseLedger();
				ledger.LedgerCode = r.LedgerCode;
				ledger.VatPercent = r.VatPercent;
				ledger.Comment = r.Comment;
				ledger.Amount = remaining;
				remaining = 0;
				ledger.LedgerAccount = accounts.find(a=>a.Code == r.LedgerCode) || null;
				return ledger;
			});
			this.setNewEmptyEditLedgerValue();
			this.validate();
		},
		getFavoriteSummary(favorite:IPurchaseLedgerFavorite):string {
			return favorite.Rows.map(r=>`${r.LedgerCode}`).join(", ");
		},
		openSaveFavoriteModal(){
			this.favoriteName = "";
			this.showFavoriteNameModal = true;
		},
		async confirmSaveFavorite(){
			let name = this.favoriteName.trim();
			if (!name || this.savingFavorite || !this.p.Ledgers.length) return;
			this.savingFavorite = true;
			try{
				let config = DossierConfigService.getBookkeepingConfig();
				let id = config.PurchaseLedgerFavorites.reduce((max, f)=>Math.max(max, f.ID), 0) + 1;
				config.PurchaseLedgerFavorites.push({
					ID: id,
					Name: name,
					Rows: this.p.Ledgers.map(l=>{
						return {LedgerCode: l.LedgerCode, VatPercent: l.VatPercent, Comment: l.Comment};
					})
				});
				this.bookkeepingConfig = await DossierConfigService.setBookkeepingConfig(config);
				this.selectedFavoriteId = id;
				this.showFavoriteNameModal = false;
				this.$wf.notify(this.$ct("favorites.saved").toString(), "success", 3000, "check");
			}finally{
				this.savingFavorite = false;
			}
		},
		async deleteFavorite(favorite:IPurchaseLedgerFavorite){
			let confirmed = await this.$wf.confirm(this.$ct("favorites.delete-confirm", {name: favorite.Name}),
				this.$t("common.yes"), this.$t("common.no"), this.$ct("common.warning"), "warning");
			if (!confirmed) return;
			let config = DossierConfigService.getBookkeepingConfig();
			let i = config.PurchaseLedgerFavorites.findIndex(f=>f.ID == favorite.ID);
			if (i != -1){
				config.PurchaseLedgerFavorites.splice(i, 1);
				this.bookkeepingConfig = await DossierConfigService.setBookkeepingConfig(config);
			}
			if (this.selectedFavoriteId == favorite.ID){
				this.selectedFavoriteId = 0;
			}
		}
	},
	mounted(){
		this.setNewEmptyEditLedgerValue();
		this.validate();
	},
	components: {
		AppPurchaseEditLedger,
		AppPurchaseEditLedgers,
		AppPurchaseEditLedgersMobile,
		AppPurchaseDocuments
	},
	i18nEx: {
		componentPrefix: "components.purchases"
	}
});
